import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Container,
  Badge,
  UncontrolledTooltip,
  Col,
  Row,
  Button,
  Card,
} from "reactstrap";
import ReactBsTable from "components/Tables/ReactBsTable";
import DynamicFormModal from "components/Modal/Form";
import { publisherFormFields } from "variables/formFields";
import { DEFAULT_STATUS_LABELS } from "variables/constants";
import { showAlert } from "components/Common/commonFunctions";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import SearchableDropdown from "components/DropDown/SearchableDropdown";
import { DATE_RANGE_OPTIONS } from "variables/constants";
import { ROLES } from "variables/constants";
import { useAuth } from "context/AuthContext";
import { DEFAULT_ROUTES } from "variables/constants";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  createPublisher,
  updatePublisher,
  getPublisherList,
  publisherResetPassword,
  getPublisherDetailsById,
} from "actions/publisher";
import NotificationAlert from "react-notification-alert";
import { STATUS_OPTIONS } from "variables/constants";
import { getProductTypeList } from "actions/productType";
import { loginAsPublisher } from "actions/users";

const sortFieldMap = {
  companyName: "CompanyName",
  pocFirstName: "PocFirstName",
  primaryContact: "PrimaryContact",
  createdAt: "CreatedAt",
};

const Publishers = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [data, setData] = useState({
    data: [],
    totalRecords: 0,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [publisherEmail, setPublisherEmail] = useState("");
  const [showLoader, setshowLoader] = useState(false);
  const [selected, setSelected] = useState(null);
  const notificationAlertRef = useRef(null);
  const [pageNum, setPageNum] = useState(1);
  const [dropDownData, setdropDownData] = useState({
    productTypeData: {
      options: [],
      totalRecords: 0,
      searchString: "",
      pageNum: 1,
    },
  });
  const [publisherOptions, setPublisherOptions] = useState([]);
  const [formState, setFormState] = useState({
    values: {
      status: true,
      siteNames: [],
      bundleIds: [],
      productType: [],
    },
    errors: {},
  });
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });

  const [filters, setFilters] = useState({
    company_name: "",
    poc_name: "",
    contact: "",
    publisher: "",
    date_range: "",
    start_date: "",
    end_date: "",
    status: "",
    filterParams: [],
  });

  const isEditMode = !!selected; // Check selected row is Edit mode or not.

  const handleRowClick = (row) => {
    navigate(`/admin/publishers/details?tab=details`, { state: row }); // Navigate to publisherDetails page.
  };

  // const handleRowClick = (row) => {
  //   navigate(`/admin/publishers/details/${row.ID}?tab=details`, { state: row });
  // };

  //=> Validators use for apply validation while typing.
  const validators = {
    email: (value) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        ? ""
        : "Enter a valid email address",

    pocPhone: (value) =>
      /^\d*$/.test(value) ? "" : "Phone number should contain only numbers",
  };

  //==> validateMultipleEmails function is used for repoting email to validate email by regex.
  const validateMultipleEmails = (value) => {
    if (!value) return "";

    const emails = value
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const invalidEmails = emails.filter((email) => !emailRegex.test(email));

    return invalidEmails.length
      ? `Invalid email(s): ${invalidEmails.join(", ")}`
      : "";
  };

  // => HandleChange function used for set form values in a state.
  const handleChange = (e) => {
    const { name, value } = e.target;
    let error = "";

    // 🔹 Multiple email validation
    if (name === "reportingEmail") {
      error = validateMultipleEmails(value);
    }

    // 🔹 Email validation
    if (name === "email" && value) {
      error = validators.email(value);
    }

    // 🔹 Phone validation
    if (name === "pocPhone") {
      const numericValue = value.replace(/\D/g, "");

      error = validators.pocPhone(numericValue);
      setFormState((prev) => ({
        values: { ...prev.values, [name]: numericValue },
        errors: { ...prev.errors, [name]: error },
      }));
      return;
    }

    // 🔹 Default fields
    setFormState((prev) => ({
      values: { ...prev.values, [name]: value },
      errors: { ...prev.errors, [name]: error },
    }));
  };

  //===> Publisher List Api call
  const getPublisherListData = async () => {
    try {
      setshowLoader(true);
      const res = await getPublisherList(
        pageNum,
        filters.filterParams,
        "",
        sortConfig.sortField,
        sortConfig.sortOrder,
      );
      if (res.code === 200) {
        setshowLoader(false);
        setData({
          data: res?.data?.Publisher,
          totalRecords: res?.data?.totalRecords,
        }); // Set List Data according to api response.
        // notify("success", res?.message);
      } else {
        setshowLoader(false);
        setData({
          ...data,
          data: [],
        });
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      setshowLoader(false);
    }
  };

  useEffect(() => {
    getPublisherListData();
  }, [pageNum, filters.filterParams, sortConfig.sortField, sortConfig.sortOrder]);

  const getPublisherOptions = async (searchString = "") => {
    try {
      const res = await getPublisherList(1, [], searchString);
      if (res.code === 200) {
        const options =
          res?.data?.Publisher?.map((publisher) => ({
            label: publisher.companyName,
            value: publisher.ID,
          })) || [];
        setPublisherOptions(options);
      }
    } catch (error) {
      console.error("Failed to load publisher options", error);
    }
  };

  useEffect(() => {
    getPublisherOptions();
  }, []);

  const handlePublisherSearch = useCallback(({ query }) => {
    getPublisherOptions(query);
  }, []);

  //=== Publisher List Api call

  //=> handleStatusToggle function used for activate and deactivate the user with confirmation.
  const handleStatusToggle = async (row) => {
    const isActive = row.status === "ACTIVE";
    const res = await getPublisherDetailsById(row?.ID);
    let data = res?.data;
    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this publisher?`,
      message: `Publisher "${row.companyName}" will be ${
        isActive ? "inactive" : "active"
      }.`,
      type: "warning",
      confirmStyle: "secondary",
      cancelStyle: "btn btn-primary btn-gradient-primary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => confirmStatus(data),
      onCancel: () => {},
    });
  };

  //============Login as a Publisher functionality ==============
  // const handleLogin = async (e) => {
  //   // e.preventDefault();
  //   // if (!validate()) return;
  //   let form = {
  //     email: "publisher@truelift.com",
  //     password: "123",
  //   };
  //   const res = await login(form);

  //   if (!res.success) {
  //     // notify("danger", res.message);
  //     return;
  //   }

  //   // get stored user from `p`
  //   const auth = JSON.parse(localStorage.getItem("p"));

  //   // role validation
  //   const matchedKey = Object.keys(ROLES).find(
  //     (key) => ROLES[key] === auth.role,
  //   );

  //   const redirect = DEFAULT_ROUTES[matchedKey] || "/admin/dashboard";

  //   setTimeout(() => {
  //     navigate(redirect);
  //   }, 800);
  // };

  const handleLoginAsPublisher = async (email) => {
    try {
      const data = {
        email: email,
      };
      const res = await loginAsPublisher(data);
      if (res?.code === 200 && res?.data) {
        // Store current admin data in p1
        const currentAdminData = localStorage.getItem("p");
        if (currentAdminData) {
          localStorage.setItem("p1", currentAdminData);
        }

        // Set new publisher data as p
        localStorage.setItem("p", JSON.stringify(res.data));

        notify("success", "Logged in as publisher successfully");

        // Force a hard reload to re-initialize the app with new data
        setTimeout(() => {
          window.location.href = "/publisher/dashboard";
        }, 800);
      } else {
        notify("danger", res?.message || "Failed to login as publisher");
      }
    } catch (error) {
      console.error("Error logging in as publisher:", error);
      notify("danger", "Something went wrong");
    }
  };

  const loginaspublisher = (row) => {
    showAlert({
      title: `Are you sure you want to login as a publisher?`,
      message: ``,
      type: "warning",
      confirmStyle: "secondary",
      cancelStyle: "btn btn-primary btn-gradient-primary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => {
        handleLoginAsPublisher(row.email);
      },
      onCancel: () => {},
    });
  };
  //============Login as a Publisher functionality ==============

  //==> handleResetPassword function used for confirmation modal on reset password.

  const handleResetPassword = (row) => {
    showAlert({
      title: "Reset Password?",
      message: `Password for "${row.companyName}" will be reset.`,
      type: "warning",
      confirmStyle: "secondary",
      cancelStyle: "btn btn-primary btn-gradient-primary",
      confirmText: "Reset",
      cancelText: "Cancel",
      onConfirm: () => {
        resetPassword(row);
      },
    });
  };

  //====> Publisher Reset Password Api

  const resetPassword = async (data) => {
    try {
      const res = await publisherResetPassword(data?.ID);
      if (res.code === 200) {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Mail Sent on you email";
        notify("success", message);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message);
    }
  };

  //===============================

  //===> confirmStatus function used for activate or deactivate the user with api call.
  const confirmStatus = async (data) => {
    try {
      let payload = {
        productTypeID: Array.isArray(data.productType)
          ? data.productType.map((item) => item.ID)
          : [],

        status: data.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
      };
      const res = await updatePublisher(data?.ID, payload);
      if (res.code === 200) {
        notify("success", res?.message);
        getPublisherListData();
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {}
  };
  //==================================================

  // const validateForm = () => {
  //   const errors = {};
  //   publisherFormFields.forEach((f) => {
  //     if (f.required && !formState.values[f.name]) {
  //       errors[f.name] = `${f.label} is required`;
  //     }
  //   });
  //   setFormState((p) => ({ ...p, errors }));
  //   return Object.keys(errors).length === 0;
  // };

  // ==> validateForm is used for check required field validation on submit.
  const validateForm = () => {
    let newErrors = {};

    /* --------------------------------
     GENERIC FIELD VALIDATION
  --------------------------------- */
    publisherFormFields(dropDownData.productTypeData.options).forEach(
      (field) => {
        const { name, label, required, validate } = field;

        // ⛔ Skip reportingEmail (custom validation below)
        if (name === "reportingEmail") return;

        const value = formState.values[name];

        // Required validation
        if (required && !value) {
          newErrors[name] = `${label} is required`;
          return;
        }

        // Pattern validation
        if (validate?.pattern && value) {
          if (!validate.pattern.test(value)) {
            newErrors[name] = validate.message;
          }
        }
      },
    );

    /* --------------------------------
     CUSTOM reportingEmail VALIDATION
  --------------------------------- */
    const reportingEmail = formState.values.reportingEmail;

    if (!reportingEmail) {
      newErrors.reportingEmail = "Reporting Email is required";
    } else {
      const error = validateMultipleEmails(reportingEmail);
      if (error) {
        newErrors.reportingEmail = error;
      }
    }

    /* --------------------------------
     ARRAY FIELD VALIDATION
  --------------------------------- */

    // 1️⃣ siteNames
    if (
      !Array.isArray(formState.values.siteNames) ||
      formState.values.siteNames.length === 0
    ) {
      newErrors.siteNames = "At least one Site Name is required";
    }

    // 2️⃣ bundleIds
    if (
      !Array.isArray(formState.values.bundleIds) ||
      formState.values.bundleIds.length === 0
    ) {
      newErrors.bundleIds = "At least one Bundle ID is required";
    }

    if (
      !Array.isArray(formState.values.productType) ||
      formState.values.productType.length === 0
    ) {
      newErrors.productType = "At least one Product Type ID is required";
    }

    /* --------------------------------
     SET ERRORS + RETURN RESULT
  --------------------------------- */
    setFormState((prev) => ({
      ...prev,
      errors: newErrors,
    }));

    return Object.keys(newErrors).length === 0;
  };

  //==========================================================

  //===> Notify function is used for show notification on update or create publisher.
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };
  //====================

  //===> handleSubmit function is used for create and update publisher.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setshowLoader(true);
    try {
      let payload = {
        companyName: formState.values.companyName,
        address: formState.values.address,
        email: formState.values.email,
        pocPhone: formState.values.pocPhone,
        pocFirstName: formState.values.pocFirstName,
        pocLastName: formState.values.pocLastName,
        accountingEmail: formState.values.accountingEmail,
        gamNetworkID: formState.values.gamNetworkID,
        gamAdminEmail: formState.values.gamAdminEmail,
        reportingEmail: formState.values.reportingEmail,
        // productTypeID: formState.values.productType,
        isPasswordSet: !isEditMode ? "NO" : formState.values?.isPasswordSet,
        status: formState.values.status ? "ACTIVE" : "INACTIVE",

        ...(formState.values.bundleIds?.length > 0 && {
          bundleIDs: formState.values.bundleIds,
        }),

        ...(formState.values.siteNames?.length > 0 && {
          publisherSites: formState.values.siteNames,
        }),

        ...(formState.values.productType?.length > 0 && {
          productTypeID: formState.values.productType,
        }),
      };

      const res = !isEditMode
        ? await createPublisher(payload)
        : await updatePublisher(selected.ID, payload);
      if (res.code === 200) {
        notify("success", res?.message);
        getPublisherListData();
        setshowLoader(false);
        setModalOpen(false);
        setSelected(null);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
        setshowLoader(false);
        switch (res?.message[0]?.Field) {
          case "publisherSite":
            setFormState((prev) => ({
              ...prev,
              errors: { siteNames: "Publisher Site is required" },
            }));
            break;
          case "bundelIdrequest":
            setFormState((prev) => ({
              ...prev,
              errors: { bundleIds: "BundleIDs are required" },
            }));
            break;
          default:
            break;
        }
      }
    } catch (error) {
      setshowLoader(false);
      notify("danger", "Something went wrong");
    }
  };
  //========================================

  //===> getPublisherDetails function is used for get Publisher Details is in edit mode.
  const getPublisherDetails = async (row) => {
    try {
      const res = await getPublisherDetailsById(row?.ID);
      if (res.code === 200) {
        let data = res?.data;
        // Store Publisher Details in state and show on screen
        setFormState((prev) => ({
          ...prev,
          values: {
            status: data.status === "ACTIVE" ? true : false,
            siteNames: data?.publisherSites || [],
            bundleIds: data?.publisherBundleIDs || [],
            productType: data?.productType?.map((pt) => pt.ID) || [],
            companyName: data?.companyName || "",
            address: data?.address || "",
            email: data?.email || "",
            pocPhone: data?.pocPhone || "",
            pocFirstName: data?.pocFirstName || "",
            pocLastName: data?.pocLastName || "",
            accountingEmail: data?.accountingEmail || "",
            gamNetworkID: data?.gamNetworkID || "",
            gamAdminEmail: data?.gamAdminEmail || "",
            reportingEmail: data?.reportingEmail || "",
          },
          errors: {},
        }));
        // notify("success", res?.message);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {}
  };

  const handleEditBtn = (row) => {
    setSelected(row);
    getPublisherDetails(row);
    setModalOpen(true);
  };

  //=======================================================

  const handleSortChange = (dataField, sortOrder) => {
    const mappedSortField = sortFieldMap[dataField] || dataField;
    
    // Update sort config state
    setSortConfig({ 
      dataField, 
      sortField: mappedSortField, 
      sortOrder: sortOrder || "asc" 
    });
  };

  // ===> columns is used for to show the columns on publisherlist.
  const columns = [
    // {
    //   text: "Sr. No.",
    //   dataField: "srNO",
    //   // formatter: (_, __, i) => i + 1, //formatter(cellValue, rowData, rowIndex) and _ means cellValue unused, __ means rowData unused
    //   formatter: (_, __, rowIndex) => (pageNum - 1) * 10 + rowIndex + 1,
    // },
    {
      dataField: "companyName",
      text: "Publisher",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "pocFirstName",
      text: "POC Name",
      formatter: (cell, row) => (
        <div>
          {/* <i className="fa-solid fa-user mr-2 text-grey" /> */}
          {row.pocFirstName} {row.pocLastName}
        </div>
      ),
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "primaryEmail",
      text: "Primary Contact",
      formatter: (cell, row) => (
        <div>
          <div>
            <i className="fa-solid fa-envelope mr-1 text-muted" />
            {row.email}
          </div>
          {row.pocPhone && (
            <div className="text-muted small">
              <i className="fa-solid fa-phone mr-1 text-muted" />
              {row.pocPhone}
            </div>
          )}
        </div>
      ),
      
    },

    {
      dataField: "createdAt",
      text: "Created On",
      formatter: (cell) => moment(cell).format("DD MMM YYYY"),
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell) => (
        <Badge color={cell === "ACTIVE" ? "success" : "danger"}>{cell}</Badge>
      ),
    },
    {
      text: "Actions",
      dataField: "actions",
      formatter: (_, row) => {
        const rowId = row?.ID;
        return (
          <>
            <a
              id={`view${rowId}`}
              className="table-action table-action-info"
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(row);
              }}
            >
              <i className="fas fa-eye" />
            </a>
            <UncontrolledTooltip target={`view${rowId}`}>
              View Details
            </UncontrolledTooltip>
            <a
              id={`edit${rowId}`}
              className="table-action table-action-info"
              onClick={(e) => {
                e.stopPropagation();
                handleEditBtn(row);
              }}
            >
              <i className="fas fa-edit" />
            </a>
            <UncontrolledTooltip target={`edit${rowId}`}>
              Edit
            </UncontrolledTooltip>

            <a
              id={`reset${rowId}`}
              className="table-action table-action-warning ml-2"
              onClick={(e) => {
                e.stopPropagation();
                handleResetPassword(row);
              }}
            >
              <i className="fa-solid fa-key" />
            </a>
            <UncontrolledTooltip target={`reset${rowId}`}>
              Reset Password
            </UncontrolledTooltip>

            <a
              className={`table-action table-action-delete ${
                row.status === "ACTIVE" ? "status-active" : "status-inactive"
              }`}
              id={`tooltipToggle${rowId}`}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusToggle(row);
              }}
            >
              {row.status === "ACTIVE" ? (
                <i className="fa-solid fa-xmark" />
              ) : (
                <i className="fa-solid fa-check" />
              )}
            </a>
            <UncontrolledTooltip delay={0} target={`tooltipToggle${rowId}`}>
              {row.status === "ACTIVE" ? "Mark Inactive" : "Mark Active"}
            </UncontrolledTooltip>
            <a
              className="table-action table-action-info"
              id={`asPublisher${rowId}`}
              onClick={(e) => {
                e.stopPropagation();
                loginaspublisher(row);
              }}
            >
              <i className="fa fa-sign-in" />
            </a>
            <UncontrolledTooltip delay={0} target={`asPublisher${rowId}`}>
              Login as Publisher
            </UncontrolledTooltip>
          </>
        );
      },
    },
  ];
  //===================================================================

  // ==> getDropDownData function is used for get productType list and patch to dropdown.
  const getDropDownData = async () => {
    try {
      const res = await getProductTypeList(
        0,
        `[{"field":"Status","condition":"eq","filterValues":["ACTIVE"]}]`,
        dropDownData.productTypeData.searchString,
      );
      const productTypeData =
        res?.code === 200
          ? (res?.data?.ProductType || [])
              .filter((item) => item?.status === "ACTIVE")
              .map((item) => ({
                label: item?.name || "",
                value: item?.ID,
              }))
          : [];
      setdropDownData((prev) => ({
        ...prev,
        productTypeData: { ...prev.productTypeData, options: productTypeData },
      }));
    } catch (error) {}
  };

  useEffect(() => {
    getDropDownData();
  }, []);

  //================================================================

  //===> formatDate function is used for to show date in human redable in filter.
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ===> showCustomDateInputs function is used for set start Date and end Date according to the selection of Date Range dropdown values.
  const showCustomDateInputs = (name, value) => {
    // handleChange(key, value);
    const today = moment().format("YYYY-MM-DD");

    if (value === "Custom Range") {
      setFilters((prev) => ({
        ...prev,
        date_range: value,
        start_date: "",
        end_date: "",
      }));
      // setShowCustomDate(true);
      return;
    }

    let months = 0;
    if (value === "Last 1 Month") months = 1;
    if (value === "Last 3 Month") months = 3;
    if (value === "Last 6 Month") months = 6;
    if (value === "Last 12 Month") months = 12;

    const startDate = moment().subtract(months, "months").format("YYYY-MM-DD");

    setFilters((prev) => ({
      ...prev,
      date_range: value,
      start_date: startDate,
      end_date: today,
    }));
    // setShowCustomDate(true);
  };

  //===> handleFilterChange function ise used for set filters values in state.
  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      if (key === "start_date") {
        const startDate = value;
        const endDate = prev.end_date;

        // if end_date exists AND start_date <= end_date → keep end_date
        if (endDate && new Date(startDate) <= new Date(endDate)) {
          return {
            ...prev,
            start_date: startDate,
          };
        }

        // otherwise reset end_date
        return {
          ...prev,
          start_date: startDate,
          end_date: "",
        };
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  // ==> buildFilterParams function is used for to make a filterJson array.
  const buildFilterParams = () => {
    const filterParams = [];

    if (filters?.status !== "") {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters?.status],
      });
    }

    if (filters?.company_name !== "") {
      filterParams.push({
        field: "PublisherID",
        condition: "eq",
        filterValues: [String(filters?.company_name)],
      });
    }

    if (filters?.poc_name !== "") {
      filterParams.push({
        field: "PocName",
        condition: "like",
        filterValues: [filters?.poc_name],
      });
    }

    if (filters?.contact !== "") {
      filterParams.push({
        field: "PocPhone",
        condition: "like",
        filterValues: [filters?.contact],
      });
    }

    if (filters?.start_date !== "" && filters?.end_date !== "") {
      filterParams.push({
        field: "CreatedAt",
        condition: "btw",
        filterValues: [filters?.start_date, filters?.end_date],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : "";
  };

  // ==> isAnyFilterApplied function is used for to check is any filter is emptyor not to avoid api call.
  const isAnyFilterApplied = (filters) => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "filterParams") {
        return Array.isArray(value) && value.length > 0;
      }
      return value !== "" && value !== null && value !== undefined;
    });
  };

  //==> handleSearch function is used for search the filter
  const handleSearch = () => {
    if (!isAnyFilterApplied(filters)) return;
    const newfilterParams = buildFilterParams();

    setFilters({
      ...filters,
      filterParams: newfilterParams,
    });
    if (pageNum !== 1) setPageNum(1);
    // setData(result);
  };

  const handleReset = () => {
    if (!isAnyFilterApplied(filters)) {
      return; // 🔹 nothing to reset, prevent API call
    }

    setFilters({
      company_name: "",
      poc_name: "",
      contact: "",
      publisher: "",
      date_range: "",
      start_date: "",
      end_date: "",
      status: "",
      filterParams: [],
    });
    // setData(initialData);
  };
  ///==========================================

  //===> filterSection is used for to show filters on table.
  const filterSection = () => {
    return (
      <>
        <Row className="align-items-center">
          <Col md={2}>
            <SearchableDropdown
              name="company_name"
              options={publisherOptions}
              placeholder="Select Publisher"
              value={filters.company_name}
              onChange={(e) =>
                handleFilterChange("company_name", e.target.value)
              }
              onSearch={handlePublisherSearch}
              isSearchable={true}
            />
          </Col>

          <Col md={2}>
            <input
              className="form-control"
              placeholder={"POC Name"}
              value={filters.poc_name}
              onChange={(e) => handleFilterChange("poc_name", e.target.value)}
            />
          </Col>
          <Col md={2}>
            <input
              className="form-control"
              placeholder={"Contact"}
              value={filters.contact}
              onChange={(e) => handleFilterChange("contact", e.target.value)}
            />
          </Col>
          <Col md={2}>
            <SearchableDropdown
              options={STATUS_OPTIONS}
              placeholder="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              isSearchable={false}
              isMulti={false}
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <>
            <Col md={2}>
              <SearchableDropdown
                options={DATE_RANGE_OPTIONS}
                placeholder="Date Range"
                value={filters.date_range}
                onChange={(e) =>
                  showCustomDateInputs("date_range", e.target.value)
                }
                isSearchable={false}
              />
            </Col>
            <Col md={2}>
              <div className="datepicker-wrapper">
                <DatePicker
                  selected={
                    filters.start_date ? new Date(filters.start_date) : null
                  }
                  onChange={(date) =>
                    handleFilterChange(
                      "start_date",
                      date ? formatDate(date) : "",
                    )
                  }
                  dateFormat="dd MMM yyyy"
                  placeholderText="Start Date"
                  className="form-control custom-datepicker"
                  disabled={filters.date_range !== "Custom Range"}
                />
                <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
              </div>
            </Col>

            <Col md={2}>
              <div className="datepicker-wrapper">
                <DatePicker
                  selected={
                    filters.end_date ? new Date(filters.end_date) : null
                  }
                  onChange={(date) =>
                    handleFilterChange("end_date", date ? formatDate(date) : "")
                  }
                  dateFormat="dd MMM yyyy"
                  placeholderText="End Date"
                  className="form-control custom-datepicker"
                  /* ✅ disable until start date is selected */
                  disabled={
                    filters.date_range !== "Custom Range" || !filters.start_date
                  }
                  /* ✅ minDate must be a Date object */
                  minDate={
                    filters.start_date
                      ? new Date(filters.start_date)
                      : undefined
                  }
                />
                <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
              </div>
            </Col>
          </>
          <Col md={4}>
            <Button
              onClick={handleSearch}
              disabled={!isAnyFilterApplied(filters)}
              className="filter-btn-gradient-primary btn-md responsive"
            >
              Search
            </Button>

            <Button
              // className="filter-btn-gradient-secondary btn-md responsive"
              className="secondary"
              disabled={!isAnyFilterApplied(filters)}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container className="mt--6" fluid>
        <Card>
          <ReactBsTable
            title="Publishers"
            description="Manage your publishers"
            data={data?.data}
            columns={columns}
            keyField="ID"
            addbtntext="Add Publisher"
            onAddBtnClick={() => {
              setSelected(null);
              setFormState({
                values: {
                  status: true,
                  siteNames: [],
                  bundleIds: [],
                  productType: [],
                },
                errors: {},
              });
              setModalOpen(true);
              // navigate("/admin/publishers/add")
            }}
            handleRowClick={handleRowClick}
            renderFilters={filterSection}
            page={pageNum}
            sizePerPage={10}
            totalRecords={data?.totalRecords}
            onPageChange={(p) => setPageNum(p)}
            onSortChange={handleSortChange}
            defaultSortField={sortConfig.dataField}
            defaultSortOrder={sortConfig.sortOrder}
            showLoader={showLoader}
          />
        </Card>
      </Container>

      <DynamicFormModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        title={isEditMode ? "Edit Publisher" : "Add Publisher"}
        fields={publisherFormFields(
          dropDownData.productTypeData.options,
          isEditMode,
        )}
        formData={formState.values}
        errors={formState.errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        formState={formState}
        setFormState={setFormState}
        isEditMode={isEditMode}
        trueLabel={DEFAULT_STATUS_LABELS.active}
        falseLabel={DEFAULT_STATUS_LABELS.inactive}
        siteNames={{ show: true, error: formState?.errors?.siteNames }}
        bundleIds={{ show: true, error: formState?.errors?.bundleIds }}
        productType={{ show: true, error: formState?.errors?.productType }}
        showLoader={showLoader}
      />
    </>
  );
};

export default Publishers;